import React, { useRef, useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { api as afriApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Upload, Video, X, ShoppingBag, Sparkles, Scissors, Wand2, Hash, Image as ImageIcon, Camera, Volume2, Zap } from "lucide-react";
import { toast } from "sonner";
import { isPlyxStandaloneMode } from "@/lib/plyxStandalone";
import PlyxLiveRecorder from "@/components/plyx/PlyxLiveRecorder";
import { applyPlyxBranding } from "@/lib/plyxVideoDownload";
import { playPlyxChime } from "@/lib/plyxChime";
import { celebratePlyx } from "@/components/plyx/PlyxCelebration";
import PlyxAIStudio from "@/components/plyx/PlyxAIStudio";
import SoundLibraryDrawer from "@/components/plyx/SoundLibraryDrawer";
import { compressVideoForUpload, isCompressionSupported } from "@/lib/plyxVideoCompress";

// iter-119q: in-browser compression bumps the effective ceiling from
// 80MB → ~500MB. Files between 40MB and HARD_MAX are auto-optimised
// to ~720p H.264 before upload (typically shrinks 3-5x), so most phone
// clips will go through cleanly even on slow networks.
const MAX_MB = 500;                              // hard ceiling pre-compression
const COMPRESS_THRESHOLD_MB = 40;                // start compressing above this
const POST_COMPRESS_CAP_BYTES = 95 * 1024 * 1024; // ingress-safe (under nginx 100MB)
const MAX_DURATION = 600;

// CSS filter presets — the player applies the same `filter: ...` style
// at playback time so what the creator sees is what viewers see.
// v2 expansion: 10 face-beauty presets on top of the original 6 (the
// codes mirror PLYX_BEAUTIFY_FILTERS in /app/backend/routes/plyx_videos.py).
const BEAUTIFY_FILTERS = [
  { code: "none",       label: "None",       group: "Plain",  css: "none" },
  // Originals
  { code: "vivid",      label: "Vivid",      group: "Plain",  css: "saturate(1.4) contrast(1.1)" },
  { code: "warm",       label: "Warm",       group: "Plain",  css: "sepia(0.18) saturate(1.2) hue-rotate(-8deg)" },
  { code: "cool",       label: "Cool",       group: "Plain",  css: "saturate(1.1) hue-rotate(8deg) brightness(1.02)" },
  { code: "bw",         label: "B&W",        group: "Plain",  css: "grayscale(1) contrast(1.05)" },
  { code: "vintage",    label: "Vintage",    group: "Plain",  css: "sepia(0.5) saturate(0.85) contrast(0.95)" },
  { code: "dreamy",     label: "Dreamy",     group: "Plain",  css: "blur(0.4px) saturate(1.15) brightness(1.05) contrast(0.95)" },
  // Face-beauty v2
  { code: "smooth",     label: "Smooth",     group: "Beauty", css: "blur(0.7px) saturate(1.1) brightness(1.06) contrast(0.96)" },
  { code: "glow",       label: "Glow",       group: "Beauty", css: "brightness(1.12) saturate(1.18) contrast(1.05) hue-rotate(-4deg)" },
  { code: "porcelain",  label: "Porcelain",  group: "Beauty", css: "brightness(1.1) saturate(0.9) contrast(1.02) hue-rotate(4deg)" },
  { code: "kbeauty",    label: "K-Beauty",   group: "Beauty", css: "brightness(1.08) saturate(1.05) contrast(0.94) sepia(0.06)" },
  { code: "cinematic",  label: "Cinematic",  group: "Beauty", css: "contrast(1.18) saturate(0.92) brightness(0.97)" },
  { code: "sunset",     label: "Sunset",     group: "Beauty", css: "sepia(0.25) saturate(1.25) brightness(1.05) hue-rotate(-12deg)" },
  { code: "golden",     label: "Golden",     group: "Beauty", css: "sepia(0.45) saturate(1.3) brightness(1.06)" },
  { code: "fresh",      label: "Fresh",      group: "Beauty", css: "brightness(1.1) saturate(1.3) contrast(1.04)" },
  { code: "midnight",   label: "Midnight",   group: "Beauty", css: "brightness(0.92) saturate(1.05) contrast(1.1) hue-rotate(14deg)" },
  { code: "drama",      label: "Drama",      group: "Beauty", css: "contrast(1.3) saturate(0.7) brightness(0.96)" },
];
const FILTERS_BY_CODE = Object.fromEntries(BEAUTIFY_FILTERS.map(f => [f.code, f]));

// Curated sticker palette grouped by vibe.
const STICKER_PALETTE = [
  // Faces & sparkle
  "✨", "💫", "⭐", "🌟", "💖", "💗", "💕", "💋", "😍", "🥰", "🤩",
  // Reactions
  "🔥", "💯", "🙌", "👏", "😂", "😎", "🤯", "💀", "🤝",
  // Crowns & trophies
  "👑", "🏆", "🎖️", "🥇", "💎", "🎉", "🎊",
  // Africa & motion
  "🌍", "🌴", "🌺", "🌸", "🎶", "🥁", "🎵",
  // Animals/luck
  "🦋", "🐉", "🦁", "🦄", "🐝", "🐢",
  // Food & home
  "🍔", "🌶️", "🍯", "🍫", "🥃", "🌹",
];

// Extract a single-frame thumbnail from a local video file using canvas.
// Returns a Blob (image/jpeg) or null on failure.
const extractThumbnail = (file) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    v.muted = true;
    v.playsInline = true;
    v.crossOrigin = "anonymous";
    v.addEventListener("loadeddata", () => {
      try { v.currentTime = Math.min(0.5, (v.duration || 1) * 0.1); } catch { /* ignore */ }
    });
    v.addEventListener("seeked", () => {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth || 720;
      canvas.height = v.videoHeight || 1280;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      const finish = (blob) => { URL.revokeObjectURL(url); resolve(blob || null); };
      // Prefer WebP (~30% smaller than JPEG at the same quality) — falls
      // back to JPEG on browsers that can't encode WebP (older Safari
      // silently returns a PNG blob, which we detect via blob.type).
      canvas.toBlob((webp) => {
        if (webp && webp.type === "image/webp") return finish(webp);
        canvas.toBlob((jpeg) => finish(jpeg), "image/jpeg", 0.8);
      }, "image/webp", 0.8);
    });
    v.addEventListener("error", () => { URL.revokeObjectURL(url); resolve(null); });
  } catch { resolve(null); }
});

const getVideoDuration = (file) => new Promise((resolve) => {
  // Some codecs (HEVC on iOS Safari, exotic webm) never fire
  // `loadedmetadata` — that would hang the picker indefinitely.
  // We resolve to 0 after 8s so the upload proceeds with whatever
  // duration the backend infers via ffprobe.
  const url = URL.createObjectURL(file);
  let done = false;
  const finish = (val) => {
    if (done) return;
    done = true;
    try { URL.revokeObjectURL(url); } catch { /* noop */ }
    resolve(Number.isFinite(val) && val > 0 ? val : 0);
  };
  try {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    v.addEventListener("loadedmetadata", () => finish(v.duration));
    v.addEventListener("error", () => finish(0));
    setTimeout(() => finish(0), 8000); // hard ceiling
  } catch { finish(0); }
});

const PlyxUpload = () => {
  const navigate = useNavigate();
  const { user: plyxUser, loading: plyxLoading } = usePlyxAuth();
  const { user: afriUser } = useAuth();
  const fileRef = useRef(null);
  const videoFileRef = useRef(null);
  const photoFileRef = useRef(null);
  // iter-119m: separate refs for the native-camera launchers above so
  // they can use `capture` (opens phone camera) while the library-pick
  // inputs above stay capture-free (opens gallery).
  const cameraVideoRef = useRef(null);
  const cameraPhotoRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [duration, setDuration] = useState(null);
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState("");
  const [myListings, setMyListings] = useState([]);
  const [listingId, setListingId] = useState("");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [beautifyFilter, setBeautifyFilter] = useState("none");
  const [stickers, setStickers] = useState([]);   // [{emoji,x,y,size,rot}]
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [aiBusy, setAiBusy] = useState(null);     // "polish" | "tags" | "suggest" | null
  const [mediaType, setMediaType] = useState("video"); // "video" | "photo"
  const [liveRecorderOpen, setLiveRecorderOpen] = useState(false);
  // iter-118: AI Studio + Sound Library drawer state
  const [aiStudioOpen, setAiStudioOpen] = useState(false);
  const [soundDrawerOpen, setSoundDrawerOpen] = useState(false);
  const [pickedSound, setPickedSound] = useState(null);  // {sound_id, title, audio_url, …}
  // The Plyx signature toggle was removed — branding is now always
  // applied server-side via ffmpeg. The `brandWithPlyx` flag is
  // hard-true so the existing `if (brandWithPlyx)` upload code path
  // continues to add the `plyx_branded=1` form field.
  const brandWithPlyx = true;
  const [brandingPhase, setBrandingPhase] = useState(null);   // "preparing" | "stamping" | null
  const [brandingProgress, setBrandingProgress] = useState(0);
  // iter-119q: in-browser compression phase (before upload)
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);
  const previewBoxRef = useRef(null);

  // Load this AfriMarket user's listings so they can link one to the video.
  useEffect(() => {
    if (!afriUser) return;
    afriApi.get("/listings", { params: { owner_id: afriUser.user_id, approval: "all", limit: 100 } })
      .then((r) => setMyListings(r.data || []))
      .catch(() => { /* optional */ });
  }, [afriUser]);

  // iter-118: pick up a clip handed off from /plyx/cam or /plyx/studio
  // via sessionStorage. Convert the base64 back to a File and feed it
  // into the existing acceptFile() pipeline.
  useEffect(() => {
    const camBlob = sessionStorage.getItem("plyx_cam_blob");
    const studioBlob = sessionStorage.getItem("plyx_studio_blob");
    const handoffBlob = camBlob || studioBlob;
    if (!handoffBlob || file) return;
    try {
      const src = camBlob ? "cam" : "studio";
      const byteString = atob(handoffBlob.split(",")[1]);
      const mime = handoffBlob.split(",")[0].match(/data:([^;]+)/)?.[1] || "video/webm";
      const ab = new ArrayBuffer(byteString.length);
      const u8 = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) u8[i] = byteString.charCodeAt(i);
      const f = new File([ab], `plyx-${src}-${Date.now()}.webm`, { type: mime });
      sessionStorage.removeItem("plyx_cam_blob");
      sessionStorage.removeItem("plyx_studio_blob");
      sessionStorage.removeItem("plyx_cam_filter");
      acceptFile(f).then(() => toast.success(`Clip from Plyx ${src === "cam" ? "Cam" : "Studio"} loaded — add caption to post.`));
    } catch {
      sessionStorage.removeItem("plyx_cam_blob");
      sessionStorage.removeItem("plyx_studio_blob");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (plyxLoading) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  }
  if (!plyxUser) {
    return <Navigate to="/plyx/login" replace state={{ from: "/plyx/upload" }}/>;
  }

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await acceptFile(f);
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    setMediaType("video");
    setDuration(null);
    setProgress(0);
    setBeautifyFilter("none");
    setStickers([]);
    setTrimStart(0);
    setTrimEnd(0);
    setAiBusy(null);
    if (fileRef.current) fileRef.current.value = "";
    if (videoFileRef.current) videoFileRef.current.value = "";
    if (photoFileRef.current) photoFileRef.current.value = "";
  };

  // Accept a File from any source (file picker OR live AR recorder).
  const acceptFile = async (f) => {
    const isVideo = f.type.startsWith("video/");
    const isImage = f.type.startsWith("image/");
    if (!isVideo && !isImage) {
      toast.error("Please choose a video or photo file");
      return;
    }
    const mb = f.size / (1024 * 1024);
    const maxMb = isImage ? 30 : MAX_MB;
    if (mb > maxMb) {
      toast.error(`File is ${mb.toFixed(0)} MB — max ${maxMb} MB`);
      return;
    }
    let dur = null;
    if (isVideo) {
      dur = await getVideoDuration(f);
      if (dur > MAX_DURATION + 1) {
        toast.error(`Video is ${dur.toFixed(0)}s — max ${MAX_DURATION}s`);
        return;
      }
    }
    setFile(f);
    setMediaType(isImage ? "photo" : "video");
    setDuration(dur);
    setTrimStart(0);
    setTrimEnd(dur || 0);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(1);
    try {
      const isPhoto = mediaType === "photo";

      // Stamp the Plyx watermark + jingle bumper is now ALWAYS applied
      // server-side via the ffmpeg background task (intro + watermark
      // + outro + chime). The previous client-side canvas + MediaRecorder
      // approach was unreliable on iOS Safari (canvas tainted by CORS,
      // captureStream() missing frames) and was producing visually BLACK
      // videos for many creators. Server-side is deterministic and
      // produces the same brand bookends every time.
      let uploadFile = file;
      const brandedExt = null;
      const brandedMime = null;

      // iter-119q: compress big videos in-browser so they fit under
      // nginx's 100MB cap. Photos are tiny — never compressed. Videos
      // under 40MB are already small enough to upload directly. Above
      // that we run the canvas+MediaRecorder transcode pipeline.
      // Compress when the file is big OR its bitrate is high — typical
      // 4K/HEVC iPhone recordings under 40MB still choke playback on
      // cellular. Production has no server-side ffmpeg, so this
      // client-side squeeze to 720p H.264 is the only transcode step
      // that runs on the live site.
      const estBitrateMbps = duration ? (file.size * 8) / (duration * 1024 * 1024) : 0;
      const needsCompress = !isPhoto && (
        file.size > COMPRESS_THRESHOLD_MB * 1024 * 1024 || estBitrateMbps > 8
      );
      if (needsCompress) {
        if (!isCompressionSupported()) {
          // Old browser path — only allow if already small enough.
          if (file.size > POST_COMPRESS_CAP_BYTES) {
            toast.error(
              `Your clip is ${(file.size / (1024 * 1024)).toFixed(0)} MB. Your browser can't auto-compress — try Chrome or a newer Safari, or trim to under ${Math.floor(POST_COMPRESS_CAP_BYTES / (1024 * 1024))}MB.`,
              { duration: 7000 },
            );
            setUploading(false);
            return;
          }
        } else {
          setCompressing(true);
          setCompressProgress(0);
          try {
            const result = await compressVideoForUpload(file, {
              onProgress: (p) => setCompressProgress(p),
            });
            // Keep the original if the re-encode somehow came out bigger
            // (already-efficient sources).
            uploadFile = (result.file && result.file.size < file.size) ? result.file : file;
            if (!result.skipped && uploadFile !== file) {
              const savedPct = ((1 - result.compressedSize / result.originalSize) * 100).toFixed(0);
              toast.success(
                `Optimised your clip · ${(result.originalSize / (1024 * 1024)).toFixed(1)} MB → ${(result.compressedSize / (1024 * 1024)).toFixed(1)} MB (${savedPct}% smaller)`,
                { duration: 4000 },
              );
            }
          } catch (err) {
            // iter-119cc: DO NOT abort the upload just because the
            // in-browser compressor failed. This was killing Samsung
            // HEVC recordings — Chrome Android can't decode HEVC in
            // MediaRecorder → compression throws → upload was aborted.
            // TikTok and WhatsApp both accept the raw file and let
            // their server transcode it. We do the same: if the raw
            // file fits within the ingress cap, upload it un-compressed
            // and let ffmpeg handle format conversion server-side.
            console.warn("[Plyx Upload] client compression failed, falling back to raw:", err);
            if (file.size <= POST_COMPRESS_CAP_BYTES) {
              toast.message(
                "Uploading in original quality — server will optimise it after upload.",
                { duration: 4000 },
              );
              uploadFile = file;  // upload the raw file, no compression
            } else {
              // Truly too big to squeeze through ingress AND compression
              // couldn't help — genuine error.
              setCompressing(false);
              setUploading(false);
              toast.error(
                `Your clip is ${(file.size / (1024 * 1024)).toFixed(0)} MB and your browser can't compress this format (likely HEVC from a newer phone). Please trim to under ${Math.floor(POST_COMPRESS_CAP_BYTES / (1024 * 1024))}MB or record in H.264 mode.`,
                { duration: 8000 },
              );
              return;
            }
          }
          setCompressing(false);
        }
      }

      // Defensive final check — if compression didn't get us under the
      // ingress cap, surface a clear error before nginx returns 413.
      if (!isPhoto && uploadFile.size > POST_COMPRESS_CAP_BYTES) {
        setUploading(false);
        toast.error(
          `Even after optimisation your clip is ${(uploadFile.size / (1024 * 1024)).toFixed(0)} MB — too large. Try a shorter recording.`,
          { duration: 6000 },
        );
        return;
      }

      const fd = new FormData();
      fd.append("video", uploadFile);
      if (!isPhoto) {
        // Always pull the thumbnail from the BRANDED file so the poster
        // also shows the Plyx wordmark. Wrapped in try/catch because a
        // failure here (codec quirk, OOM on huge file) must NOT abort
        // the upload — the backend regenerates the thumb anyway.
        try {
          const thumb = await extractThumbnail(uploadFile);
          if (thumb) fd.append("thumbnail", new File([thumb], thumb.type === "image/webp" ? "thumb.webp" : "thumb.jpg", { type: thumb.type || "image/jpeg" }));
        } catch (thumbErr) {
          console.warn("[Plyx Upload] Thumbnail extraction skipped:", thumbErr?.message);
        }
      }
      fd.append("caption", caption);
      if (listingId) fd.append("listing_id", listingId);
      if (tags) fd.append("tags", tags);
      if (!isPhoto && duration) fd.append("duration_sec", String(duration + (brandedExt ? 0.7 : 0)));
      if (beautifyFilter && beautifyFilter !== "none") fd.append("beautify_filter", beautifyFilter);
      if (stickers.length > 0) fd.append("stickers", JSON.stringify(stickers));
      // Always tell the backend we want the Plyx signature — the server
      // ffmpeg pipeline produces the branded copy in the background and
      // every share/download surface gets the stamped version. The feed
      // also auto-swaps to the branded URL once the build finishes.
      fd.append("plyx_branded", "1");
      // iter-118: optional soundtrack chosen from /plyx/sound-library
      if (pickedSound?.sound_id) {
        fd.append("sound_id", pickedSound.sound_id);
        if (pickedSound.source) fd.append("sound_source", pickedSound.source);
        if (pickedSound.title) fd.append("sound_title", pickedSound.title);
        if (pickedSound.audio_url) fd.append("sound_audio_url", pickedSound.audio_url);
      }
      // Only send trim if the user actually narrowed the clip. After Plyx
      // branding the recorder already encodes the full source so trim
      // markers are ignored downstream — skip them to avoid double-trim.
      if (!isPhoto && !brandedExt && duration && (trimStart > 0.05 || trimEnd < duration - 0.05)) {
        fd.append("trim_start", String(trimStart));
        fd.append("trim_end", String(trimEnd));
      }
      // Bump to 2% immediately so the user sees motion the moment they
      // tap Post — otherwise the bar can sit at 1% for several seconds
      // while the browser packs the FormData, which feels broken.
      setProgress(2);
      const { data } = await plyxApi.post("/plyx/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // 15 min ceiling — enough for a 250 MB upload over slow 3G,
        // short enough that a dead connection eventually errors out
        // instead of spinning the UI forever.
        timeout: 15 * 60 * 1000,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.max(2, Math.round((e.loaded / e.total) * 100)));
        },
      });
      toast.success(
        isPhoto
          ? "Posted to Plyx · photo is live."
          : (brandedExt
              ? "Posted to Plyx · stamped with the Plyx signature"
              : "Posted to Plyx · auto-captions running in the background"),
        { duration: 4000 }
      );
      // Reward the upload with the Plyx ding — micro-moment that
      // builds the brand's audio identity over hundreds of uploads.
      playPlyxChime();
      navigate(`/plyx/u/${plyxUser.handle}`);
      return data;
    } catch (e) {
      // Surface the most actionable error message we can. The backend's
      // structured `detail` wins; otherwise fall back to a hint based on
      // the network/status code so the user knows what to try next.
      const detail = e?.response?.data?.detail;
      const code = e?.code;
      const status = e?.response?.status;
      let msg = "Upload failed";
      if (detail) {
        msg = detail;
      } else if (code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
        msg = "Upload timed out — check your connection and try again.";
      } else if (status === 413) {
        msg = `File too large for upload. Max ${Math.floor(POST_COMPRESS_CAP_BYTES / (1024 * 1024))}MB after auto-optimisation.`;
      } else if (status === 401) {
        msg = "Please sign in again to upload.";
      } else if (status >= 500) {
        msg = "Server hiccup — please retry in a moment.";
      } else if (e?.message) {
        msg = e.message;
      }
      console.error("[Plyx Upload] failed:", { status, code, detail, message: e?.message });
      toast.error(msg);
    } finally {
      setUploading(false);
      setBrandingPhase(null);
      setBrandingProgress(0);
      setCompressing(false);
      setCompressProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-upload-page">
      {liveRecorderOpen && (
        <PlyxLiveRecorder
          onCancel={() => setLiveRecorderOpen(false)}
          onCapture={(f) => {
            setLiveRecorderOpen(false);
            acceptFile(f).then(() => toast.success("Live recording captured — review and post."));
          }}
        />
      )}
      <div className="container-narrow py-12">
        <h1 className="font-display text-4xl sm:text-5xl mb-2 tracking-tight">Create on Plyx</h1>
        <p className="text-white/60 mb-4">Record straight from your camera, pick from your gallery, or jump into the AR booth.</p>

        {/* iter-119m: One-tap camera launchers at the TOP of the page.
            Native-camera capture via the `capture` attribute opens the
            phone's camera app directly (selfie cam for video, rear cam
            for photo). Tobechukwu's "I can't find any camera" report
            traces back to the toolbox row below being too subtle on
            mobile — these big prominent CTAs fix that immediately. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4" data-testid="plyx-upload-camera-row">
          <button
            type="button"
            onClick={() => cameraVideoRef.current?.click()}
            data-testid="plyx-upload-camera-video"
            className="px-4 py-3.5 rounded-2xl font-bold text-sm text-white inline-flex items-center justify-center gap-2 shadow-lg shadow-pink-900/30"
            style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 100%)" }}
          >
            <Video className="w-4 h-4"/> Record video
          </button>
          <button
            type="button"
            onClick={() => cameraPhotoRef.current?.click()}
            data-testid="plyx-upload-camera-photo"
            className="px-4 py-3.5 rounded-2xl font-bold text-sm text-black inline-flex items-center justify-center gap-2 shadow-lg shadow-cyan-900/30"
            style={{ background: "linear-gradient(135deg, #FFCC2E 0%, #00E0C7 100%)" }}
          >
            <Camera className="w-4 h-4"/> Take photo
          </button>
          <button
            type="button"
            onClick={() => setLiveRecorderOpen(true)}
            data-testid="plyx-upload-open-ar"
            className="px-4 py-3.5 rounded-2xl font-bold text-sm text-white inline-flex items-center justify-center gap-2 shadow-lg shadow-purple-900/30 border border-white/15"
            style={{ background: "linear-gradient(135deg, #6B21A8 0%, #FF2D7A 100%)" }}
          >
            <Sparkles className="w-4 h-4"/> AR camera
          </button>
        </div>
        {/* Native-camera inputs (hidden) — `capture` opens the device
            camera directly on iOS Safari + Android Chrome. */}
        <input ref={cameraVideoRef} type="file" accept="video/*" capture="user" hidden onChange={onPick} data-testid="plyx-upload-camera-video-input"/>
        <input ref={cameraPhotoRef} type="file" accept="image/*" capture="environment" hidden onChange={onPick} data-testid="plyx-upload-camera-photo-input"/>

        {/* iter-118 toolbox row — quick entry to Cam, Studio, AI Studio, Sound Library */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="plyx-upload-toolbox">
          <button
            type="button"
            onClick={() => navigate("/plyx/cam")}
            className="text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white inline-flex items-center gap-1.5"
            data-testid="plyx-upload-open-cam"
          ><Camera className="w-3.5 h-3.5"/> Plyx Cam</button>
          <button
            type="button"
            onClick={() => navigate("/plyx/studio")}
            className="text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500 text-white inline-flex items-center gap-1.5"
            data-testid="plyx-upload-open-studio"
          ><Scissors className="w-3.5 h-3.5"/> Studio</button>
          <button
            type="button"
            onClick={() => setAiStudioOpen(true)}
            className="text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black inline-flex items-center gap-1.5"
            data-testid="plyx-upload-open-ai-studio"
          ><Sparkles className="w-3.5 h-3.5"/> AI Studio</button>
          <button
            type="button"
            onClick={() => setSoundDrawerOpen(true)}
            className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center gap-1.5 border border-white/15"
            data-testid="plyx-upload-open-sounds"
          >
            <Volume2 className="w-3.5 h-3.5"/>
            {pickedSound ? `Sound: ${pickedSound.title.slice(0, 18)}${pickedSound.title.length > 18 ? "…" : ""}` : "Pick a sound"}
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Picker / preview */}
          <div>
            {!previewUrl ? (
              <div className="space-y-3">
                <div className="w-full aspect-[9/16] max-h-[520px] rounded-2xl border-2 border-dashed border-white/15 hover:border-[#FF2D7A] transition-colors flex flex-col items-center justify-center bg-white/5 px-6">
                  <div className="flex items-center gap-3 mb-4 text-white/40">
                    <Video className="w-8 h-8"/>
                    <span className="text-xs uppercase tracking-wider">or</span>
                    <ImageIcon className="w-8 h-8"/>
                  </div>
                  <div className="font-semibold text-center mb-1">Choose what to upload</div>
                  <div className="text-xs text-white/40 text-center mb-1">Video up to {MAX_MB}MB (auto-optimised) · Photo up to 30MB</div>
                  <div className="text-[11px] text-[#FFB42D]/85 text-center mb-5 inline-flex items-center justify-center gap-1.5">
                    <Volume2 className="w-3 h-3" />
                    Videos must have audio (music, voice or sound)
                  </div>
                  <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
                    <button
                      type="button"
                      onClick={() => videoFileRef.current?.click()}
                      data-testid="plyx-upload-pick-video"
                      className="px-4 py-2.5 rounded-full text-sm font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/15 inline-flex items-center justify-center gap-1.5"
                    >
                      <Video className="w-4 h-4"/> Video
                    </button>
                    <button
                      type="button"
                      onClick={() => photoFileRef.current?.click()}
                      data-testid="plyx-upload-pick-photo"
                      className="px-4 py-2.5 rounded-full text-sm font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/15 inline-flex items-center justify-center gap-1.5"
                    >
                      <ImageIcon className="w-4 h-4"/> Photo
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setLiveRecorderOpen(true)}
                  data-testid="plyx-upload-open-live-ar"
                  className="w-full px-4 py-3 rounded-full font-semibold text-white inline-flex items-center justify-center gap-2"
                  style={{ background: "#E85D4E" }}
                >
                  <Camera className="w-4 h-4"/> Record live with AR face filter
                </button>
              </div>
            ) : (
              <div ref={previewBoxRef} className="relative aspect-[9/16] max-h-[520px] rounded-2xl overflow-hidden bg-black">
                {mediaType === "photo" ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    data-testid="plyx-upload-preview-photo"
                    style={{ filter: FILTERS_BY_CODE[beautifyFilter]?.css || "none" }}
                  />
                ) : (
                  <video
                    src={previewUrl}
                    controls
                    className="w-full h-full"
                    data-testid="plyx-upload-preview"
                    style={{ filter: FILTERS_BY_CODE[beautifyFilter]?.css || "none" }}
                  />
                )}
                {/* Sticker overlay on preview — exact same positioning the player will use */}
                <div className="absolute inset-0 pointer-events-none">
                  {stickers.map((s, i) => (
                    <span
                      key={i}
                      className="absolute pointer-events-auto select-none cursor-pointer"
                      onDoubleClick={() => setStickers((arr) => arr.filter((_, j) => j !== i))}
                      title="Double-click to remove"
                      style={{
                        left: `${s.x * 100}%`,
                        top: `${s.y * 100}%`,
                        fontSize: s.size,
                        transform: `translate(-50%, -50%) rotate(${s.rot}deg)`,
                        textShadow: "0 0 8px rgba(0,0,0,0.4)",
                      }}
                    >
                      {s.emoji}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="plyx-upload-clear"
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-black/80"
                  aria-label="Choose different file"
                >
                  <X className="w-4 h-4"/>
                </button>
                <div className="absolute bottom-3 left-3 px-2 py-1 rounded bg-black/60 backdrop-blur text-[10px] uppercase tracking-wider">
                  {mediaType === "photo"
                    ? `Photo · ${(file.size / (1024 * 1024)).toFixed(1)}MB`
                    : `${(duration || 0).toFixed(1)}s · ${(file.size / (1024 * 1024)).toFixed(1)}MB`}
                </div>
              </div>
            )}
            <input ref={videoFileRef} type="file" accept="video/*" hidden onChange={onPick} data-testid="plyx-upload-video-input"/>
            <input ref={photoFileRef} type="file" accept="image/*" hidden onChange={onPick} data-testid="plyx-upload-photo-input"/>
            <input ref={fileRef} type="file" accept="video/*,image/*" hidden onChange={onPick} data-testid="plyx-upload-file-input"/>

            {/* Beautify panel — only shown once a video is loaded */}
            {previewUrl && (
              <div className="mt-4 space-y-4" data-testid="plyx-upload-beautify-panel">
                {["Beauty", "Plain"].map((group) => (
                  <div key={group}>
                    <label className="block text-xs uppercase tracking-wider text-white/60 mb-2 flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#FFB42D]"/>
                      {group === "Beauty" ? "Face-beauty filters" : "Classic filters"}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {BEAUTIFY_FILTERS.filter(f => f.group === group).map((f) => (
                        <button
                          key={f.code}
                          type="button"
                          onClick={() => setBeautifyFilter(f.code)}
                          data-testid={`plyx-upload-filter-${f.code}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                                      ${beautifyFilter === f.code
                                        ? "bg-white text-white border-white"
                                        : "border-white/20 text-white/80 hover:border-white/40"}`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Trim — choose start & end seconds (videos only) */}
                {mediaType === "video" && duration && (
                  <div data-testid="plyx-upload-trim-panel">
                    <label className="block text-xs uppercase tracking-wider text-white/60 mb-2 flex items-center gap-2">
                      <Scissors className="w-3.5 h-3.5 text-white/85"/> Trim
                      <span className="ml-auto text-[10px] text-white/40 normal-case">
                        {`${trimStart.toFixed(1)}s → ${trimEnd.toFixed(1)}s · ${(trimEnd - trimStart).toFixed(1)}s clip`}
                      </span>
                    </label>
                    <div className="space-y-2">
                      <div>
                        <div className="text-[10px] text-white/50 mb-1">Start</div>
                        <input
                          type="range"
                          min={0}
                          max={Math.max(0.1, duration - 0.1)}
                          step={0.1}
                          value={trimStart}
                          onChange={(e) => {
                            const v = Math.min(parseFloat(e.target.value), trimEnd - 0.5);
                            setTrimStart(Math.max(0, v));
                          }}
                          data-testid="plyx-upload-trim-start"
                          className="w-full accent-[#00E0C7]"
                        />
                      </div>
                      <div>
                        <div className="text-[10px] text-white/50 mb-1">End</div>
                        <input
                          type="range"
                          min={0.5}
                          max={duration}
                          step={0.1}
                          value={trimEnd}
                          onChange={(e) => {
                            const v = Math.max(parseFloat(e.target.value), trimStart + 0.5);
                            setTrimEnd(Math.min(duration, v));
                          }}
                          data-testid="plyx-upload-trim-end"
                          className="w-full accent-[#FF2D7A]"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Emoji stickers */}
                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <label className="text-xs uppercase tracking-wider text-white/60">Emoji stickers ({stickers.length}/12)</label>
                    {stickers.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setStickers([])}
                        data-testid="plyx-upload-stickers-clear"
                        className="text-[11px] text-white/50 hover:text-white"
                      >Clear all</button>
                    )}
                  </div>
                  <div className="grid grid-cols-9 gap-1 max-h-40 overflow-y-auto p-1 rounded-md bg-white/5 border border-white/10">
                    {STICKER_PALETTE.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => {
                          if (stickers.length >= 12) {
                            toast.error("You can add up to 12 stickers per video.");
                            return;
                          }
                          // Drop near the centre with a tiny random offset so they don't all stack.
                          const jx = 0.5 + (Math.random() - 0.5) * 0.3;
                          const jy = 0.5 + (Math.random() - 0.5) * 0.3;
                          const rot = Math.floor((Math.random() - 0.5) * 20);
                          setStickers((arr) => [...arr, { emoji, x: jx, y: jy, size: 48, rot }]);
                        }}
                        data-testid={`plyx-upload-sticker-${emoji}`}
                        className="w-9 h-9 rounded-md hover:bg-white/10 text-2xl flex items-center justify-center"
                        title={`Add ${emoji}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  {stickers.length > 0 && (
                    <p className="text-[10px] text-white/40 mt-1.5">Tip: double-click a sticker on the preview to remove it.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs uppercase tracking-wider text-white/60">Caption</label>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={aiBusy === "suggest"}
                    onClick={async () => {
                      setAiBusy("suggest");
                      try {
                        const hashtags = (tags || "")
                          .split(",")
                          .map((t) => t.trim())
                          .filter(Boolean);
                        const r = await plyxApi.post("/plyx/ai/suggest-caption", {
                          draft: caption.trim(),
                          hashtags,
                        });
                        if (r.data?.suggestion) {
                          setCaption(r.data.suggestion);
                          toast.success("AI drafted a caption for you ✨");
                        }
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "AI suggest failed");
                      } finally { setAiBusy(null); }
                    }}
                    data-testid="plyx-upload-ai-suggest"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border border-[#00E0C7]/50 text-white/85 hover:bg-white/10/10 disabled:opacity-40"
                  >
                    {aiBusy === "suggest" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>}
                    Suggest
                  </button>
                  <button
                    type="button"
                    disabled={!caption.trim() || aiBusy === "polish"}
                    onClick={async () => {
                      setAiBusy("polish");
                      try {
                        const r = await plyxApi.post("/plyx/ai/caption-polish", {
                          caption: caption.trim(),
                        });
                        if (r.data?.polished) {
                          setCaption(r.data.polished);
                          toast.success("Caption polished ✨");
                        }
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "AI polish failed");
                      } finally { setAiBusy(null); }
                    }}
                    data-testid="plyx-upload-ai-polish"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold text-white disabled:opacity-40"
                    style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 100%)" }}
                  >
                    {aiBusy === "polish" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Wand2 className="w-3 h-3"/>}
                    Polish
                  </button>
                </div>
              </div>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 2200))}
                rows={4}
                placeholder="Tell people what's in your video…"
                data-testid="plyx-upload-caption"
                className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              />
              <div className="text-xs text-white/40 mt-1">{caption.length}/2200</div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs uppercase tracking-wider text-white/60">Tags (comma-separated)</label>
                <button
                  type="button"
                  disabled={!caption.trim() || aiBusy === "tags"}
                  onClick={async () => {
                    setAiBusy("tags");
                    try {
                      const r = await plyxApi.post("/plyx/ai/hashtags", {
                        caption: caption.trim(),
                      });
                      const tagList = (r.data?.hashtags || []);
                      if (tagList.length) {
                        setTags(tagList.join(", "));
                        toast.success(`AI added ${tagList.length} hashtags`);
                      } else {
                        toast.info("AI returned no hashtags — try a longer caption.");
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "AI hashtag failed");
                    } finally { setAiBusy(null); }
                  }}
                  data-testid="plyx-upload-ai-hashtags"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold text-white disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg, #00E0C7 0%, #FFB42D 100%)" }}
                >
                  {aiBusy === "tags" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Hash className="w-3 h-3"/>}
                  AI suggest
                </button>
              </div>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="ankara, lagos, owambe"
                data-testid="plyx-upload-tags"
                className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              />
              {/* Trending tags — one-tap append. Free (cached server-side
                  for 6h so this costs ~0 LLM credits). iter-115. */}
              <TrendingTagSuggestions
                currentTags={tags}
                onAppend={(tag) => {
                  const existing = (tags || "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
                  if (existing.includes(tag.toLowerCase())) return;
                  setTags(existing.length ? `${tags}, ${tag}` : tag);
                }}
              />
            </div>

            {/* Link an AfriMarket listing — hidden in standalone Plyx preview */}
            {!isPlyxStandaloneMode() && (afriUser ? (
              myListings.length > 0 ? (
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5 flex items-center gap-2">
                    <ShoppingBag className="w-3.5 h-3.5"/> Link an AfriMarket listing (optional)
                  </label>
                  <select
                    value={listingId}
                    onChange={(e) => setListingId(e.target.value)}
                    data-testid="plyx-upload-listing-select"
                    className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
                  >
                    <option value="">— No listing —</option>
                    {myListings.map((l) => (
                      <option key={l.listing_id} value={l.listing_id}>{l.title} · {l.currency} {l.price}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="p-3 rounded-md bg-white/5 text-xs text-white/60 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-[#FFB42D]"/>
                  <span>You don&apos;t have any AfriMarket listings yet. Create one to enable &ldquo;Tap to buy&rdquo; on your Plyx videos.</span>
                </div>
              )
            ) : (
              <div className="p-3 rounded-md bg-white/5 text-xs text-white/60">
                Link your AfriMarket account in your Plyx profile to enable &ldquo;Tap to buy&rdquo; on your videos.
              </div>
            ))}

            <div
              className="flex items-start gap-3 p-3 rounded-2xl bg-white/[0.04] border border-white/10"
              data-testid="plyx-brand-info-row"
            >
              <div
                className="mt-0.5 w-4 h-4 rounded-sm flex items-center justify-center text-[10px] font-black"
                style={{ background: "#E85D4E", color: "#0A0A0A" }}
                aria-hidden
              >✓</div>
              <div className="text-sm">
                <div className="font-semibold text-white flex items-center gap-2">
                  <span
                    className="font-display"
                    style={{
                      backgroundImage: "#E85D4E",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >Plyx signature is on</span>
                </div>
                <p className="text-white/60 mt-1 text-xs leading-snug">
                  Every clip you post automatically gets the Plyx wordmark + a short Plyx sound bumper at the start and end. Your <span className="font-semibold text-white">@{plyxUser.handle}</span> handle stays in the corner so your clip carries the brand wherever it gets shared.
                </p>
              </div>
            </div>

            <button
              type="button"
              disabled={!file || uploading || compressing}
              onClick={submit}
              data-testid="plyx-upload-submit"
              className="w-full py-3 rounded-full font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ background: "#E85D4E" }}
            >
              {compressing
                ? <><Zap className="w-4 h-4 animate-pulse"/> Optimising your clip… {Math.round(compressProgress * 100)}%</>
                : uploading
                ? (brandingPhase === "stamping"
                    ? <><Loader2 className="w-4 h-4 animate-spin"/> Stamping Plyx signature {Math.round(brandingProgress * 100)}%…</>
                    : <><Loader2 className="w-4 h-4 animate-spin"/> Uploading {progress}%…</>)
                : <><Upload className="w-4 h-4"/> Post to Plyx</>}
            </button>
            {(uploading || compressing) && (
              <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="plyx-upload-progress-bar">
                <div
                  className="h-full transition-all"
                  style={{
                    width: compressing
                      ? `${Math.max(2, Math.round(compressProgress * 100))}%`
                      : brandingPhase === "stamping"
                      ? `${Math.round(brandingProgress * 100)}%`
                      : `${progress}%`,
                    background: compressing
                      ? "linear-gradient(90deg, #FFB42D, #00E0C7)"
                      : "linear-gradient(90deg, #FF2D7A, #FFB42D, #00E0C7)",
                  }}
                />
              </div>
            )}
            {compressing && (
              <p className="text-[11px] text-white/55 text-center mt-1">
                Re-encoding in your browser so the upload fits — usually 15-30s.
              </p>
            )}
          </div>
        </div>
      </div>
      {/* iter-118 drawers */}
      <PlyxAIStudio
        open={aiStudioOpen}
        onClose={() => setAiStudioOpen(false)}
        presetCaption={caption}
      />
      <SoundLibraryDrawer
        open={soundDrawerOpen}
        onClose={() => setSoundDrawerOpen(false)}
        onPick={(s) => setPickedSound(s)}
        selectedId={pickedSound?.sound_id}
      />
    </div>
  );
};

export default PlyxUpload;

/**
 * TrendingTagSuggestions (iter-115) — surfaces the top 5 trending Plyx
 * tags from /plyx/ai/trends-explained with one-tap append. The endpoint
 * caches results for 6h server-side so this is effectively free.
 * Hidden when the API returns nothing (e.g. no recent posts).
 */
const TrendingTagSuggestions = ({ currentTags, onAppend }) => {
  const [trends, setTrends] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await plyxApi.get("/plyx/ai/trends-explained");
        if (alive) setTrends(r.data?.trends || []);
      } catch { /* silent — non-critical */ }
    })();
    return () => { alive = false; };
  }, []);
  if (trends.length === 0) return null;
  const currentLower = (currentTags || "").toLowerCase();
  return (
    <div className="mt-2" data-testid="plyx-upload-trending-tags">
      <div className="flex items-center justify-between mb-1.5">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/55 font-bold">
          <Sparkles className="w-3 h-3 text-[#FFB42D]" />
          Trending on Plyx this week
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="plyx-upload-trending-why-toggle"
          className="text-[10px] text-white/45 hover:text-white/80"
        >
          {open ? "hide why" : "why?"}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {trends.slice(0, 6).map((t) => {
          const already = currentLower.includes(t.tag);
          return (
            <button
              key={t.tag}
              type="button"
              onClick={() => onAppend(t.tag)}
              disabled={already}
              data-testid={`plyx-upload-trend-${t.tag}`}
              className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 border ${
                already
                  ? "bg-white/10 border-white/15 text-white/40 cursor-not-allowed"
                  : "bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/10 border-[#FF2D7A]/30 text-[#FFB42D] hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/20"
              }`}
            >
              #{t.tag}
              <span className="text-[9px] text-white/40">·{t.count}</span>
            </button>
          );
        })}
      </div>
      {open && (
        <ul className="mt-2 space-y-1.5" data-testid="plyx-upload-trending-why-list">
          {trends.slice(0, 6).map((t) => (
            <li key={t.tag} className="text-[10px] text-white/60 leading-tight">
              <span className="text-[#FFB42D] font-semibold">#{t.tag}:</span> {t.why}
              {t.angle ? <span className="text-white/40"> · Try: {t.angle}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
